// Permissões do copiloto (STU-149, regras de 28/10/2026):
//  CRIAR recurso novo (agente, tabela, relatório, coletor, ferramenta, evento) → sem
//    perguntar (auto: true);
//  ALTERAR fluxo/agente que o copiloto criou → sem perguntar; que ele NÃO criou →
//    pergunta (o fluxo: uma vez por conversa — ver CopilotPanel flowGuard);
//  APAGAR e disparar coisa real (publicar, ligar, ativar/zerar coletor) → pergunta.
// O backend tem a mesma classificação para o MCP (services/McpService/toolRisk.ts).

const q = v => `"${String(v || "").trim()}"`;

export const COPILOT_PERMISSIONS = {
  publish_flow: {
    risk: "sensitive",
    title: "Publicar o fluxo?",
    summary: () => "O fluxo vai para o ar e passa a rodar de verdade (atender, ligar, enviar).",
    confirm: "Publicar",
  },
  place_call: {
    risk: "sensitive",
    title: "Fazer a ligação?",
    summary: a => `Vou ligar agora para ${a.to || "o número informado"}, e a ligação vai rodar este fluxo.`,
    confirm: "Ligar",
  },
  publish_collector: {
    risk: "sensitive",
    title: "Ativar o coletor?",
    summary: () => "O coletor passa a vigiar o banco sozinho e dispara a automação a cada registro novo.",
    confirm: "Ativar",
  },
  reset_collector: {
    risk: "sensitive",
    title: "Zerar o coletor?",
    summary: () =>
      "O coletor volta a ler os registros mais recentes como se fosse a primeira vez — as automações ligadas a ele podem rodar de novo (reenviar ou religar).",
    confirm: "Zerar",
  },
  create_dataset: {
    risk: "write",
    auto: true, // criar recurso novo: sem perguntar
    title: "Criar a tabela?",
    summary: a => {
      const n = Array.isArray(a.fields) ? a.fields.length : 0;
      return `Vou criar a tabela ${q(a.name)}${n ? ` com ${n} coluna${n > 1 ? "s" : ""}` : ""} na sua conta.`;
    },
    confirm: "Criar tabela",
  },
  create_report: {
    risk: "write",
    auto: true, // criar recurso novo: sem perguntar
    title: "Salvar o relatório?",
    summary: a => `Vou salvar o relatório ${q(a.name)} — ele fica na tela de Relatórios.`,
    confirm: "Salvar relatório",
  },
  create_collector: {
    risk: "write",
    auto: true, // criar recurso novo: sem perguntar
    title: "Criar o coletor?",
    summary: a => `Vou criar o coletor ${q(a.name)}${a.pollEvery ? `, que consulta o banco a cada ${a.pollEvery}` : ""}. Ele só começa a rodar quando for ativado.`,
    confirm: "Criar coletor",
  },
  create_tool: {
    risk: "write",
    auto: true, // criar recurso novo: sem perguntar
    title: "Criar a ferramenta?",
    summary: a => `Vou criar a ferramenta ${q(a.name)} e ligar neste agente.${a.url ? ` Ela chama ${a.url}.` : ""}`,
    confirm: "Criar ferramenta",
  },
  insert_record: {
    risk: "write",
    auto: true, // cadastrar linhas numa tabela: sem perguntar
    title: "Cadastrar na tabela?",
    summary: () => "Vou cadastrar uma linha na tabela.",
    confirm: "Cadastrar",
  },
  create_agent: {
    risk: "write",
    auto: true, // criar recurso novo: sem perguntar
    title: "Criar o agente?",
    summary: a => `Vou criar o agente de IA ${q(a.name)}${a.provider ? ` (voz ${a.provider}${a.voice ? ` · ${a.voice}` : ""})` : ""}. Ele aparece em Agentes e você pode ajustar depois.`,
    confirm: "Criar agente",
  },
  update_agent: {
    risk: "write",
    title: "Alterar o agente?",
    summary: a => {
      const what = [
        a.name !== undefined && "nome",
        a.instructions !== undefined && "instruções",
        a.greeting !== undefined && "saudação",
        (a.provider !== undefined || a.voice !== undefined) && "voz",
        a.toolIds !== undefined && "ferramentas",
        a.transferDestinations !== undefined && "transferências",
        a.outputs !== undefined && "campos de resultado",
        a.transferToHuman !== undefined && "transferência para humano",
        a.recordCalls !== undefined && "gravação",
      ].filter(Boolean);
      return `Vou alterar ${what.length ? what.join(", ") : "a configuração"} do agente. Fica salvo como uma nova versão (dá para voltar em Versões).`;
    },
    confirm: "Alterar agente",
  },
  create_agent_event: {
    risk: "write",
    auto: true, // criar recurso novo: sem perguntar
    title: "Configurar o evento?",
    summary: a => `Quando acontecer "${a.event}" no agente, vou enviar para ${a.destinoType === "dataset" ? "uma tabela" : a.destinoType === "fluxo" ? "um fluxo" : a.destinoType === "espelho" ? "o Numera Atende (espelho)" : "um conector"}.`,
    confirm: "Configurar",
  },
};

// ação do backend sem resumo próprio: pede confirmação genérica se altera algo
const GENERIC = {
  write: { risk: "write", title: "Confirmar a alteração?", summary: () => "A IA quer alterar algo na sua conta.", confirm: "Confirmar" },
  sensitive: { risk: "sensitive", title: "Confirmar a ação?", summary: () => "A IA quer executar uma ação que dispara algo de verdade.", confirm: "Confirmar" },
};

// risk (opcional) = classificação vinda do backend (/ai/account-tools)
// null = roda direto. `auto` (criar recurso novo) não pede confirmação, mas a ação
// ainda é registrada no backend.
export const permissionFor = (name, risk) => {
  const p = COPILOT_PERMISSIONS[name] || (risk && GENERIC[risk]) || null;
  return p && p.auto ? null : p;
};

// ferramentas que editam o FLUXO aberto: sem perguntar se o fluxo é do copiloto;
// senão, uma confirmação por conversa
export const FLOW_EDIT_TOOLS = ["set_trigger", "add_node", "connect", "update_node", "delete_node", "set_name", "set_notes", "propose_whatsapp_template"];
export const FLOW_EDIT_PERMISSION = name => ({
  risk: "write",
  title: "Alterar este fluxo?",
  summary: () => `O fluxo ${name ? `"${name}"` : "aberto"} não foi criado pelo copiloto. Posso fazer as alterações nele? (vale para esta conversa)`,
  confirm: "Pode alterar",
});
